/**
 * VendorLinkDialog - Relationship type selector for vendor linking
 *
 * Allows users to specify the relationship type when linking vendors:
 * - Same Entity / DBA
 * - Subsidiary of
 * - Acquired by
 * - Rebranded from
 */

import { useState } from "react";
import { Building2, ArrowRight, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

export interface VendorLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorA: { id: number; name: string };
  vendorB: { id: number; name: string };
  onConfirm: (relationshipType: string, primaryId?: number) => void;
}

type RelationshipType = "same_entity" | "subsidiary" | "acquired" | "rebranded";

interface RelationshipOption {
  value: RelationshipType;
  label: string;
  description: string;
  showDirection: boolean;
}

const RELATIONSHIP_OPTIONS: RelationshipOption[] = [
  {
    value: "same_entity",
    label: "Same Entity / DBA",
    description:
      'Both names refer to the same company (different legal names or "doing business as")',
    showDirection: false,
  },
  {
    value: "subsidiary",
    label: "Subsidiary of",
    description: "One vendor is a subsidiary or child company of the other",
    showDirection: true,
  },
  {
    value: "acquired",
    label: "Acquired by",
    description: "One vendor was acquired by or merged into the other",
    showDirection: true,
  },
  {
    value: "rebranded",
    label: "Rebranded from",
    description:
      "One vendor rebranded or changed their name to become the other",
    showDirection: true,
  },
];

export function VendorLinkDialog({
  open,
  onOpenChange,
  vendorA,
  vendorB,
  onConfirm,
}: VendorLinkDialogProps) {
  const [relationshipType, setRelationshipType] =
    useState<RelationshipType>("same_entity");
  const [parentVendorId, setParentVendorId] = useState<number>(vendorA.id);
  const [isPrimary, setIsPrimary] = useState(false);

  const selectedOption = RELATIONSHIP_OPTIONS.find(
    o => o.value === relationshipType
  );

  const handleConfirm = () => {
    const primaryId =
      relationshipType === "same_entity"
        ? isPrimary
          ? parentVendorId
          : undefined
        : parentVendorId;
    onConfirm(relationshipType, primaryId);
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setRelationshipType("same_entity");
      setParentVendorId(vendorA.id);
      setIsPrimary(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Link Vendors</DialogTitle>
          <DialogDescription>
            Define the relationship between these two vendors.
          </DialogDescription>
        </DialogHeader>

        {/* Vendor names */}
        <div className="flex items-center justify-center gap-4 py-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">{vendorA.name}</span>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">{vendorB.name}</span>
          </div>
        </div>

        <Separator />

        {/* Relationship type selection */}
        <div className="space-y-4">
          <Label>Relationship Type</Label>
          <RadioGroup
            value={relationshipType}
            onValueChange={value =>
              setRelationshipType(value as RelationshipType)
            }
            className="space-y-3"
          >
            {RELATIONSHIP_OPTIONS.map(option => (
              <div
                key={option.value}
                className={cn(
                  "flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-colors",
                  relationshipType === option.value
                    ? "border-[var(--primary)] bg-[var(--primary)]/5"
                    : "border-border hover:bg-muted/50"
                )}
                onClick={() => setRelationshipType(option.value)}
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  className="mt-1"
                />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={option.value}
                    className="font-medium cursor-pointer"
                  >
                    {option.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {option.description}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Direction selector for directional relationships */}
        {selectedOption?.showDirection && (
          <>
            <Separator />
            <div className="space-y-3">
              <Label>Which is the parent/primary?</Label>
              <RadioGroup
                value={String(parentVendorId)}
                onValueChange={value => setParentVendorId(Number(value))}
                className="flex gap-4"
              >
                <div
                  className={cn(
                    "flex-1 flex items-center gap-2 p-3 rounded-md border cursor-pointer",
                    parentVendorId === vendorA.id
                      ? "border-[var(--primary)] bg-[var(--primary)]/5"
                      : "border-border"
                  )}
                  onClick={() => setParentVendorId(vendorA.id)}
                >
                  <RadioGroupItem
                    value={String(vendorA.id)}
                    id={`parent-${vendorA.id}`}
                  />
                  <Label
                    htmlFor={`parent-${vendorA.id}`}
                    className="cursor-pointer truncate"
                  >
                    {vendorA.name}
                  </Label>
                </div>
                <div
                  className={cn(
                    "flex-1 flex items-center gap-2 p-3 rounded-md border cursor-pointer",
                    parentVendorId === vendorB.id
                      ? "border-[var(--primary)] bg-[var(--primary)]/5"
                      : "border-border"
                  )}
                  onClick={() => setParentVendorId(vendorB.id)}
                >
                  <RadioGroupItem
                    value={String(vendorB.id)}
                    id={`parent-${vendorB.id}`}
                  />
                  <Label
                    htmlFor={`parent-${vendorB.id}`}
                    className="cursor-pointer truncate"
                  >
                    {vendorB.name}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </>
        )}

        {/* Primary toggle for same_entity */}
        {relationshipType === "same_entity" && (
          <>
            <Separator />
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label htmlFor="primary-toggle">Set primary vendor</Label>
                <p className="text-xs text-muted-foreground">
                  Mark one vendor as the canonical/preferred name
                </p>
              </div>
              <Switch
                id="primary-toggle"
                checked={isPrimary}
                onCheckedChange={setIsPrimary}
              />
            </div>
            {isPrimary && (
              <RadioGroup
                value={String(parentVendorId)}
                onValueChange={value => setParentVendorId(Number(value))}
                className="flex gap-4"
              >
                <div
                  className={cn(
                    "flex-1 flex items-center gap-2 p-2 rounded-md border cursor-pointer",
                    parentVendorId === vendorA.id
                      ? "border-[var(--primary)] bg-[var(--primary)]/5"
                      : "border-border"
                  )}
                  onClick={() => setParentVendorId(vendorA.id)}
                >
                  <RadioGroupItem
                    value={String(vendorA.id)}
                    id={`primary-a-${vendorA.id}`}
                  />
                  <Label
                    htmlFor={`primary-a-${vendorA.id}`}
                    className="cursor-pointer text-sm truncate"
                  >
                    {vendorA.name}
                  </Label>
                </div>
                <div
                  className={cn(
                    "flex-1 flex items-center gap-2 p-2 rounded-md border cursor-pointer",
                    parentVendorId === vendorB.id
                      ? "border-[var(--primary)] bg-[var(--primary)]/5"
                      : "border-border"
                  )}
                  onClick={() => setParentVendorId(vendorB.id)}
                >
                  <RadioGroupItem
                    value={String(vendorB.id)}
                    id={`primary-a-${vendorB.id}`}
                  />
                  <Label
                    htmlFor={`primary-a-${vendorB.id}`}
                    className="cursor-pointer text-sm truncate"
                  >
                    {vendorB.name}
                  </Label>
                </div>
              </RadioGroup>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            <Check className="w-4 h-4 mr-2" />
            Link Vendors
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VendorLinkDialog;
